import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Bell,
  CalendarDays,
  Hash,
  Image as ImageIcon,
  Info,
  Loader2,
  MessageSquareText,
  Pin,
  Plus,
  Search,
  Shield,
  Users,
  X,
} from "lucide-react";

import {
  fetchClub,
  fetchClubArtworks,
  fetchClubEvents,
  createClubEvent,
  cancelClubEvent,
  fetchMyClubs,
  joinClub,
  leaveClub,
  archiveClub,
} from "../../services/clubsApi";
import { useAuth } from "../../context/AuthContext";
import ClubMembersTab from "../../pages/Clubs/ClubMembersTab";
import ClubInfoRail from "../../components/Clubs/ClubInfoRail";
import ClubFeedPanel from "../../components/Clubs/ClubFeedPanel";
import ClubFeedCreatePostModal from "../../components/Clubs/ClubFeedCreatePostModal";
import ClubChannelsEnvironment from "../../components/Clubs/ClubChannelsEnvironment";
import UnifiedPostComposer from "../../features/posts/UnifiedPostComposer";
import { getV2CreatePostId } from "../../features/posts/mapV2CreateResultToThreadNavState";

/** When true, club create-post uses UnifiedPostComposer + POST /api/v2/posts. Default off for rollback. */
function isClubUnifiedComposerEnabled() {
  const v = String(process.env.REACT_APP_CLUB_UNIFIED_COMPOSER || "").trim().toLowerCase();
  return v === "1" || v === "true" || v === "yes";
}

/* ----------------------------
   Safe helpers
----------------------------- */
function n(x) {
  return Number.isFinite(Number(x)) ? Number(x) : null;
}

function pickFirstNonNull(...vals) {
  for (const v of vals) {
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return null;
}

function getClubCounts(club) {
  const memberCount =
    n(club?.member_count) ??
    n(club?.members_count) ??
    n(club?.members) ??
    n(club?.memberCount) ??
    null;

  const watcherCount =
    n(club?.watcher_count) ??
    n(club?.watchers_count) ??
    n(club?.watchers) ??
    n(club?.watcherCount) ??
    null;

  const postCount =
    n(club?.post_count) ??
    n(club?.posts_count) ??
    n(club?.artwork_count) ??
    n(club?.artworks_count) ??
    n(club?.postCount) ??
    null;

  return {
    memberCount,
    watcherCount,
    postCount,
  };
}

function bumpCountField(prevClub, fieldCandidates, delta) {
  if (!prevClub) return prevClub;
  const next = { ...prevClub };
  for (const key of fieldCandidates) {
    const cur = n(next?.[key]);
    if (cur != null) {
      next[key] = cur + delta;
      return next;
    }
  }
  return prevClub;
}

function isTrueBool(x) {
  if (x === true) return true;
  if (x === false || x == null) return false;
  const s = String(x).trim().toLowerCase();
  return s === "true" || s === "t" || s === "1" || s === "yes";
}

function getArchivedFlag(club) {
  const direct =
    club?.is_deleted ??
    club?.isDeleted ??
    club?.deleted ??
    club?.is_archived ??
    club?.isArchived;
  if (direct != null) return isTrueBool(direct);
  const archivedAt =
    club?.archived_at ?? club?.archivedAt ?? club?.deleted_at ?? club?.deletedAt;
  return Boolean(archivedAt);
}

function getOwnerUserId(club) {
  return pickFirstNonNull(
    club?.owner_user_id,
    club?.ownerUserId,
    club?.owner_id,
    club?.ownerId,
    club?.created_by,
    club?.createdBy,
    club?.created_by_user_id,
    club?.createdByUserId
  );
}

function isSameUserId(a, b) {
  if (a == null || b == null) return false;
  const na = n(a);
  const nb = n(b);
  if (na != null && nb != null) return Number(na) === Number(nb);
  return String(a).trim() === String(b).trim();
}

function normalizeMembershipFromJoin(data) {
  if (!data || typeof data !== "object") return { role: "member", status: "active" };
  const status = String(data.status ?? data.membership?.status ?? "active").toLowerCase();
  const role = String(data.role ?? data.membership?.role ?? "member").toLowerCase();
  return { role, status: status || "active" };
}

function cn(...xs) {
  return xs.filter(Boolean).join(" ");
}

function loadClubDraft(slug) {
  if (!slug) return null;

  try {
    const raw = localStorage.getItem(`clubDraft:${slug}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function getApiBase() {
  const envBase = (process.env.REACT_APP_API_BASE || "").trim();
  if (envBase) return envBase.replace(/\/+$/, "");
  if (typeof window === "undefined") return "";
  const { protocol, hostname, port, origin } = window.location;
  if (
    (hostname === "localhost" || hostname === "127.0.0.1") &&
    (port === "3000" || port === "5173")
  ) {
    return `${protocol}//${hostname}:5000`;
  }
  return origin;
}

function resolveImageUrl(url) {
  if (!url) return null;

  let s = String(url).trim();
  if (!s) return null;

  if (s.startsWith("undefined/")) s = s.replace(/^undefined\/+/, "");
  if (/^(https?:)?\/\//i.test(s)) return s;
  if (/^(data:|blob:)/i.test(s)) return s;

  const r2Base = (process.env.REACT_APP_R2_PUBLIC_BASE || "").trim();
  if (r2Base && /^clubs\//i.test(s)) {
    return `${r2Base.replace(/\/+$/, "")}/${s.replace(/^\/+/, "")}`;
  }

  const rooted = s.startsWith("/") ? s : `/${s}`;
  const apiBase = getApiBase();
  return apiBase ? `${apiBase}${rooted}` : rooted;
}

const KNOWN_TABS = ["feed", "gallery", "channels", "events", "about"];

/** Map query value to canonical tab id, or null if unknown. */
function normalizeTabQuery(raw) {
  if (raw == null) return null;
  const t = String(raw).trim().toLowerCase();
  if (!t) return null;
  if (t === "posts") return "feed";
  if (t === "chat") return "channels";
  return KNOWN_TABS.includes(t) ? t : null;
}

function allowedTabsForModules(modules) {
  const out = [];
  if (modules.discussions) out.push("feed");
  if (modules.artFeed) out.push("gallery");
  if (modules.channels) out.push("channels");
  // Events entry point exists in the top action area and should always be navigable.
  out.push("events");
  out.push("about");
  return out;
}

/**
 * First tab when landing on /home. Gallery is opened from the header, not this row.
 */
function defaultTabForAllowed(allowed) {
  if (allowed.includes("feed")) return "feed";
  if (allowed.includes("channels")) return "channels";
  if (allowed.includes("events")) return "events";
  return "about";
}

/* ----------------------------
   UI bits
----------------------------- */
function IconButton({ children, className = "", ...props }) {
  return (
    <button
      type="button"
      className={cn(
        "h-10 w-10 rounded-2xl border border-white/10 bg-[#0c1730] text-white/80",
        "inline-flex items-center justify-center transition hover:border-white/18 hover:bg-[#102041] hover:text-white",
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
}

function TopActionButton({ active, children, className = "", ...props }) {
  return (
    <button
      type="button"
      className={cn(
        "h-10 rounded-2xl border px-4 text-sm font-medium transition",
        active
          ? "border-sky-300/25 bg-sky-400/10 text-sky-100"
          : "border-white/10 bg-[#0c1730] text-white/85 hover:border-white/18 hover:bg-[#102041]",
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
}

function SectionCard({ className = "", children }) {
  return (
    <div
      className={cn(
        "rounded-[28px] border border-white/10 bg-[#061329] overflow-hidden",
        className
      )}
    >
      {children}
    </div>
  );
}

function SectionHeader({ title, subtitle, right }) {
  return (
    <div className="flex items-start justify-between gap-4 p-5 sm:p-6 border-b border-white/10">
      <div>
        <div className="text-white text-xl font-semibold tracking-tight">{title}</div>
        {subtitle ? (
          <div className="mt-2 text-sm text-white/60 leading-relaxed">{subtitle}</div>
        ) : null}
      </div>
      {right ? <div className="shrink-0">{right}</div> : null}
    </div>
  );
}

function StatPill({ label, value }) {
  return (
    <div className="min-w-[108px] rounded-3xl border border-white/10 bg-[#09182f] px-4 py-3">
      <div className="text-[10px] uppercase tracking-[0.24em] text-white/45">{label}</div>
      <div className="mt-2 text-lg font-semibold text-white">{value}</div>
    </div>
  );
}

function EmptyPanel({ title, body }) {
  return (
    <SectionCard>
      <div className="p-6">
        <div className="text-white font-semibold tracking-tight">{title}</div>
        <div className="mt-2 text-sm text-white/60 leading-relaxed">{body}</div>
      </div>
    </SectionCard>
  );
}

function ClubArtworkTile({ a }) {
  const title = a?.title ?? a?.name ?? "Untitled";
  const img = resolveImageUrl(
    a?.image_url ?? a?.imageUrl ?? a?.thumbnail_url ?? a?.thumbUrl ?? null
  );
  return (
    <div className="group rounded-2xl border border-white/10 bg-[#08162b] overflow-hidden hover:border-white/18 transition">
      <div className="relative aspect-[4/3] bg-white/5">
        {img ? (
          <img
            src={img}
            alt=""
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
            loading="lazy"
            draggable={false}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-white/40">
            <ImageIcon className="h-5 w-5" />
          </div>
        )}
      </div>
      <div className="p-3">
        <div className="text-sm font-semibold text-white/90 tracking-tight line-clamp-1">{title}</div>
      </div>
    </div>
  );
}

function ClubGalleryTab({ slug }) {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [gErr, setGErr] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setGErr("");
    fetchClubArtworks(slug, { page: 1, pageSize: 24 })
      .then((r) => {
        if (!alive) return;
        const list = r?.items ?? r?.artworks ?? [];
        setItems(Array.isArray(list) ? list : []);
        setHasMore(Boolean(r?.hasMore ?? r?.has_next ?? r?.hasNext));
        setPage(1);
      })
      .catch((e) => {
        if (!alive) return;
        setGErr(e?.message || "Failed to load gallery.");
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [slug]);

  const onLoadMore = async () => {
    if (loadingMore || !hasMore) return;
    const next = page + 1;
    try {
      setLoadingMore(true);
      const r = await fetchClubArtworks(slug, { page: next, pageSize: 24 });
      const list = r?.items ?? r?.artworks ?? [];
      setItems((prev) => [...prev, ...(Array.isArray(list) ? list : [])]);
      setHasMore(Boolean(r?.hasMore ?? r?.has_next ?? r?.hasNext));
      setPage(next);
    } catch (e) {
      setGErr(e?.message || "Failed to load more.");
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="p-4 sm:p-5 xl:p-6">
      <SectionCard>
        <SectionHeader
          title="Gallery"
          subtitle="Artwork shared in this club."
          right={
            <IconButton title="Upload (coming soon)">
              <Plus className="h-4 w-4" />
            </IconButton>
          }
        />
        <div className="p-4 sm:p-5">
          {loading ? (
            <div className="text-white/60 text-sm py-8 text-center">Loading gallery…</div>
          ) : gErr ? (
            <div className="text-red-200 text-sm">{gErr}</div>
          ) : items.length === 0 ? (
            <div className="text-white/55 text-sm">No artwork in this club yet.</div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
                {items.map((a, idx) => (
                  <ClubArtworkTile key={a?.id ?? a?.artwork_id ?? idx} a={a} />
                ))}
              </div>
              {hasMore ? (
                <div className="mt-6 flex justify-center">
                  <button
                    type="button"
                    disabled={loadingMore}
                    onClick={onLoadMore}
                    className="h-11 px-6 rounded-2xl border border-white/12 bg-[#0b1c37] text-white/85 font-medium transition hover:border-white/18 hover:bg-[#112546] disabled:opacity-50"
                  >
                    {loadingMore ? "Loading…" : "Load more"}
                  </button>
                </div>
              ) : null}
            </>
          )}
        </div>
      </SectionCard>
    </div>
  );
}

function ClubEventsTab({ slug, club, counts, membership }) {
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [eventsErr, setEventsErr] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState("");
  const [createSuccess, setCreateSuccess] = useState("");

  const [cancelingId, setCancelingId] = useState(null);
  const [cancelErr, setCancelErr] = useState("");
  const [cancelErrEventId, setCancelErrEventId] = useState(null);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startsLocal, setStartsLocal] = useState(() => {
    const d = new Date();
    d.setHours(d.getHours() + 1);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  });
  const [endsLocal, setEndsLocal] = useState("");
  const [locationOrLink, setLocationOrLink] = useState("");

  const canCreate = useMemo(() => {
    const r = String(membership?.role || "").toLowerCase();
    return ["owner", "moderator", "mod", "admin"].includes(r);
  }, [membership]);

  const activeEventCount = useMemo(() => {
    return events.filter((e) => !Boolean(e?.is_cancelled)).length;
  }, [events]);

  const groupedEvents = useMemo(() => {
    const now = Date.now();
    const upcoming = [];
    const past = [];
    const cancelled = [];

    for (const ev of events) {
      if (Boolean(ev?.is_cancelled)) {
        cancelled.push(ev);
        continue;
      }

      const t = ev?.starts_at ? new Date(ev.starts_at).getTime() : NaN;
      if (Number.isFinite(t) && t >= now) {
        upcoming.push(ev);
      } else {
        past.push(ev);
      }
    }

    return { upcoming, past, cancelled };
  }, [events]);

  function formatDateTime(iso) {
    const d = iso ? new Date(iso) : null;
    if (!d || Number.isNaN(d.getTime())) return "";
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  }

  function toLocalIso(datetimeLocalValue) {
    // datetime-local is interpreted as local time by JS Date parsing.
    const d = new Date(datetimeLocalValue);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString();
  }

  const loadEvents = useCallback(async () => {
    if (!slug) return;
    setLoadingEvents(true);
    setEventsErr("");
    try {
      const data = await fetchClubEvents(slug);
      const list = Array.isArray(data?.events) ? data.events : [];
      setEvents(list);
    } catch (e) {
      setEventsErr(e?.message || "Failed to load events");
      setEvents([]);
    } finally {
      setLoadingEvents(false);
    }
  }, [slug]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const onCreateEvent = async (e) => {
    e.preventDefault();
    if (!slug || !canCreate) return;

    const t = title.trim();
    if (!t) {
      setCreateErr("Event title is required.");
      return;
    }

    const starts_at = toLocalIso(startsLocal);
    if (!starts_at) {
      setCreateErr("Please select a valid start date/time.");
      return;
    }

    const ends_at = endsLocal.trim() ? toLocalIso(endsLocal) : null;
    if (endsLocal.trim() && !ends_at) {
      setCreateErr("Please select a valid end date/time, or leave it empty.");
      return;
    }

    setCreating(true);
    setCreateErr("");
    setCreateSuccess("");
    try {
      await createClubEvent(slug, {
        title: t,
        description: description.trim() ? description.trim() : null,
        starts_at,
        ends_at,
        location_or_link: locationOrLink.trim() ? locationOrLink.trim() : null,
      });

      setCreateOpen(false);
      setTitle("");
      setDescription("");
      setEndsLocal("");
      setLocationOrLink("");
      await loadEvents();
      setCreateSuccess("Event created.");
    } catch (err) {
      setCreateErr(err?.message || "Failed to create event.");
    } finally {
      setCreating(false);
    }
  };

  const onCancelEvent = async (eventId) => {
    if (!slug || !canCreate) return;

    const ok = window.confirm(
      "Cancel this event?\n\nMembers will still see it in the list, marked as cancelled."
    );
    if (!ok) return;

    setCancelingId(eventId);
    setCancelErr("");
    setCancelErrEventId(eventId);
    try {
      await cancelClubEvent(slug, eventId);
      await loadEvents();
    } catch (err) {
      setCancelErr(err?.message || "Failed to cancel event.");
      setCancelErrEventId(eventId);
    } finally {
      setCancelingId(null);
    }
  };

  function ClubEventCard({ event }) {
    const startsLabel = formatDateTime(event.starts_at);
    const endsLabel = event.ends_at ? formatDateTime(event.ends_at) : "";
    const range = endsLabel ? `${startsLabel} - ${endsLabel}` : startsLabel;
    const loc = event.location_or_link || "";

    const isHttp = /^https?:\/\//i.test(loc);
    const cancelled = Boolean(event?.is_cancelled);

    return (
      <SectionCard className="mb-5">
        <div className="p-5 sm:p-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
            <div className="min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <div
                  className={cn(
                    "text-2xl font-semibold tracking-tight",
                    cancelled ? "text-white/60 line-through" : "text-white"
                  )}
                >
                  {event.title}
                </div>
                {cancelled ? (
                  <span className="rounded-full border border-red-300/25 bg-red-500/10 px-3 py-1 text-[11px] text-red-100 uppercase tracking-[0.12em]">
                    Cancelled
                  </span>
                ) : null}
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-white/70">
                <div className="inline-flex items-center gap-2">
                  <CalendarDays className="h-4 w-4 text-sky-200" />
                  <span>{range}</span>
                </div>
                {loc ? (
                  <div className="inline-flex items-center gap-2">
                    <Pin className="h-4 w-4 text-sky-200" />
                    {isHttp ? (
                      <a
                        href={loc}
                        target="_blank"
                        rel="noreferrer"
                        className="text-white/80 hover:text-white underline underline-offset-2"
                      >
                        {loc}
                      </a>
                    ) : (
                      <span>{loc}</span>
                    )}
                  </div>
                ) : null}
              </div>
              {event.description ? (
                <div className="mt-5 text-white/70 leading-relaxed whitespace-pre-wrap">
                  {event.description}
                </div>
              ) : null}
            </div>

            {canCreate ? (
              <div className="w-full lg:w-[180px] shrink-0 space-y-3">
                {!cancelled ? (
                  <button
                    type="button"
                    disabled={cancelingId === event.id}
                    onClick={() => onCancelEvent(event.id)}
                    className="h-11 w-full rounded-2xl border border-amber-300/25 bg-amber-500/10 text-amber-100 font-semibold transition hover:bg-amber-500/15 disabled:opacity-50"
                  >
                    {cancelingId === event.id ? "Cancelling…" : "Cancel event"}
                  </button>
                ) : (
                  <div className="text-sm text-white/55 leading-relaxed">
                    This event is cancelled.
                  </div>
                )}

                {cancelErr && cancelErrEventId === event.id && cancelingId === null ? (
                  <div className="rounded-xl border border-red-400/25 bg-red-500/10 px-3 py-2 text-sm text-red-200">
                    {cancelErr}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </SectionCard>
    );
  }

  if (loadingEvents) {
    return (
      <div className="p-4 sm:p-5 xl:p-6">
        <SectionCard>
          <div className="p-6 text-white/60 text-sm">Loading events…</div>
        </SectionCard>
      </div>
    );
  }

  if (eventsErr) {
    return (
      <div className="p-4 sm:p-5 xl:p-6">
        <div className="rounded-2xl border border-red-400/20 bg-red-500/10 px-4 py-3 text-sm text-red-200">
          {eventsErr}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="p-4 sm:p-5 xl:p-6 grid grid-cols-1 xl:grid-cols-12 gap-5">
        <div className="xl:col-span-8">
          <SectionCard className="mb-5">
            <div className="p-5 sm:p-6">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="rounded-full border border-sky-300/15 bg-sky-400/10 px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-sky-100">
                      Club Events
                    </span>
                  </div>

                  <div className="mt-5 text-white text-4xl font-semibold tracking-tight">
                    Events for <span className="text-sky-300">{club?.name || "this club"}</span>
                  </div>

                  <div className="mt-4 max-w-2xl text-white/65 leading-relaxed">
                    Scheduled activities stay organized so members know what’s happening and when.
                  </div>
                </div>

                <div className="flex gap-3 flex-wrap">
                  <StatPill
                    label="Scheduled"
                    value={`${activeEventCount} event${activeEventCount === 1 ? "" : "s"}`}
                  />
                  <StatPill
                    label="Members"
                    value={counts.memberCount != null ? counts.memberCount.toLocaleString() : "—"}
                  />

                  {canCreate ? (
                    <button
                      type="button"
                      onClick={() => {
                        setCreateErr("");
                        setCreateOpen(true);
                      }}
                      disabled={creating}
                      className="h-11 px-4 rounded-2xl border border-white/10 bg-[#0b1c37] text-white font-semibold transition hover:bg-[#112546] disabled:opacity-40"
                    >
                      Create event
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </SectionCard>

          {createSuccess ? (
            <div className="mt-3 rounded-2xl border border-emerald-400/20 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">
              {createSuccess}
            </div>
          ) : null}

          {events.length === 0 ? (
            <EmptyPanel
              title="No club events scheduled"
              body="Watch parties and other scheduled club activity will appear here when they’re posted."
            />
          ) : (
            <>
              {groupedEvents.upcoming.length ? (
                <div className="mt-2 mb-2 px-1 text-[11px] font-bold uppercase tracking-[0.18em] text-white/45">
                  Upcoming
                </div>
              ) : null}
              {groupedEvents.upcoming.map((ev) => (
                <ClubEventCard key={ev.id} event={ev} />
              ))}

              {groupedEvents.past.length ? (
                <div className="mt-5 mb-2 px-1 text-[11px] font-bold uppercase tracking-[0.18em] text-white/45">
                  Past
                </div>
              ) : null}
              {groupedEvents.past.map((ev) => (
                <ClubEventCard key={ev.id} event={ev} />
              ))}

              {groupedEvents.cancelled.length ? (
                <div className="mt-5 mb-2 px-1 text-[11px] font-bold uppercase tracking-[0.18em] text-white/45">
                  Cancelled
                </div>
              ) : null}
              {groupedEvents.cancelled.map((ev) => (
                <ClubEventCard key={ev.id} event={ev} />
              ))}
            </>
          )}
        </div>

        <div className="xl:col-span-4 space-y-5">
          <SectionCard>
            <div className="p-5 sm:p-6">
              <div className="text-white text-xl font-semibold tracking-tight">Event Ideas</div>
              <div className="mt-4 text-white/65 leading-relaxed">
                Keep the club active with intentional moments—watch parties, meetups, and themed sessions.
              </div>
              <div className="mt-5 space-y-3">
                {["Watch parties", "Episode discussions", "Character polls", "Fan art challenges"].map((item) => (
                  <button
                    key={item}
                    type="button"
                    className="w-full h-12 rounded-2xl border border-white/12 bg-[#09182f] px-4 text-left text-white/90 transition hover:border-white/18 hover:bg-[#102041]"
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>
          </SectionCard>

          <SectionCard>
            <div className="p-5 sm:p-6">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-2xl border border-white/10 bg-[#0b1c37] text-sky-100 inline-flex items-center justify-center">
                  <Bell className="h-4 w-4" />
                </div>
                <div className="text-white text-xl font-semibold tracking-tight">Why it works</div>
              </div>
              <div className="mt-5 space-y-4 text-white/75 leading-relaxed">
                <p>Scheduled events reduce confusion and make it easier to show up together.</p>
                <p>
                  They create rhythm, boost participation, and give members something to look forward to.
                </p>
              </div>
            </div>
          </SectionCard>
        </div>
      </div>

      {createOpen ? (
        <div className="fixed inset-0 z-[10020] flex items-center justify-center px-4 py-8">
          <button
            type="button"
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => !creating && setCreateOpen(false)}
            aria-label="Close create event"
          />
          <div className="relative z-[10021] w-full max-w-lg rounded-2xl border border-white/12 bg-[#0a1020] p-5 shadow-2xl overflow-hidden">
            <div className="flex items-start justify-between gap-3 border-b border-white/10 pb-4">
              <div className="min-w-0">
                <div className="text-white font-semibold text-lg tracking-tight">Create club event</div>
                <p className="mt-1 text-sm text-white/55 leading-relaxed">
                  Set a title and time so members can plan and show up for your scheduled activity.
                </p>
              </div>
              <button
                type="button"
                disabled={creating}
                onClick={() => !creating && setCreateOpen(false)}
                className="h-10 w-10 rounded-xl border border-white/12 bg-white/[0.04] text-white/75 inline-flex items-center justify-center hover:text-white hover:bg-white/[0.07] transition disabled:opacity-40"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={onCreateEvent} className="mt-4 space-y-4">
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-white/45 mb-2">
                  Title
                </label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={150}
                  className="w-full h-11 rounded-xl border border-white/12 bg-black/30 px-4 text-sm text-white outline-none focus:border-white/20"
                  placeholder="e.g. Weekly watch night"
                  autoFocus
                  disabled={creating}
                />
              </div>

              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-white/45 mb-2">
                  Description (optional)
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  maxLength={5000}
                  className="w-full resize-y rounded-xl border border-white/12 bg-black/30 px-4 py-3 text-sm text-white outline-none focus:border-white/20 disabled:opacity-60"
                  placeholder="Add details, rules, or what members should bring."
                  disabled={creating}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-white/45 mb-2">
                    Start
                  </label>
                  <input
                    type="datetime-local"
                    value={startsLocal}
                    onChange={(e) => setStartsLocal(e.target.value)}
                    className="w-full h-11 rounded-xl border border-white/12 bg-black/30 px-3 text-sm text-white outline-none focus:border-white/20 disabled:opacity-60"
                    disabled={creating}
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-white/45 mb-2">
                    End (optional)
                  </label>
                  <input
                    type="datetime-local"
                    value={endsLocal}
                    onChange={(e) => setEndsLocal(e.target.value)}
                    className="w-full h-11 rounded-xl border border-white/12 bg-black/30 px-3 text-sm text-white outline-none focus:border-white/20 disabled:opacity-60"
                    disabled={creating}
                  />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-white/45 mb-2">
                  Location / Link (optional)
                </label>
                <input
                  value={locationOrLink}
                  onChange={(e) => setLocationOrLink(e.target.value)}
                  maxLength={500}
                  className="w-full h-11 rounded-xl border border-white/12 bg-black/30 px-4 text-sm text-white outline-none focus:border-white/20 disabled:opacity-60"
                  placeholder="Discord channel, meeting link, or place"
                  disabled={creating}
                />
              </div>

              {createErr ? (
                <div className="rounded-xl border border-red-400/25 bg-red-500/10 px-3 py-2 text-sm text-red-200">
                  {createErr}
                </div>
              ) : null}

              <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
                <button
                  type="button"
                  disabled={creating}
                  onClick={() => !creating && setCreateOpen(false)}
                  className="h-11 px-4 rounded-xl border border-white/12 text-sm text-white/80 hover:bg-white/[0.06] transition disabled:opacity-40"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="h-11 px-5 rounded-xl border border-emerald-400/35 bg-emerald-500/15 text-emerald-100 font-semibold transition hover:bg-emerald-500/20 disabled:opacity-40"
                >
                  {creating ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Creating…
                    </span>
                  ) : (
                    "Create"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </>
  );
}

function ClubAboutTab({ club, counts, draft }) {
  const rules =
    draft?.settings?.rules ||
    club?.rules ||
    [
      "Respect other members and keep discussion constructive.",
      "Stay on-topic and post content that fits the club identity.",
      "No harassment, spam, or intentionally disruptive behavior.",
    ];

  const creator =
    pickFirstNonNull(
      club?.owner_username,
      club?.owner_name,
      club?.created_by_username,
      club?.createdByUsername,
      club?.creator_username
    ) || "Unknown";

  const clubType =
    pickFirstNonNull(club?.club_type, club?.clubType, club?.type) || "Community";

  return (
    <div className="p-4 sm:p-5 xl:p-6 grid grid-cols-1 xl:grid-cols-12 gap-5">
      <div className="xl:col-span-8 space-y-5">
        <SectionCard>
          <div className="p-5 sm:p-6">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="rounded-full border border-sky-300/15 bg-sky-400/10 px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-sky-100">
                Club About
              </span>
            </div>

            <div className="mt-5 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
              <div>
                <div className="text-white text-4xl font-semibold tracking-tight">
                  About <span className="text-sky-300">{club?.name || "this club"}</span>
                </div>
                <div className="mt-4 max-w-2xl text-white/65 leading-relaxed">
                  Learn what the club is about, what kind of community it is meant to be,
                  and what members should expect when they join.
                </div>
              </div>

              <div className="flex gap-3 flex-wrap">
                <StatPill label="Club Type" value={clubType} />
                <StatPill
                  label="Members"
                  value={
                    counts.memberCount != null
                      ? counts.memberCount.toLocaleString()
                      : "—"
                  }
                />
                <StatPill label="Created By" value={creator} />
              </div>
            </div>
          </div>
        </SectionCard>

        <SectionCard>
          <div className="p-5 sm:p-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-2xl border border-white/10 bg-[#0b1c37] text-sky-100 inline-flex items-center justify-center">
                <Info className="h-4 w-4" />
              </div>
              <div className="text-white text-xl font-semibold tracking-tight">
                Club Description
              </div>
            </div>

            <div className="mt-5 rounded-[24px] border border-white/12 bg-[#09182f] px-5 py-5 text-white/85 leading-relaxed">
              {club?.description || "No description yet."}
            </div>
          </div>
        </SectionCard>

        <SectionCard>
          <div className="p-5 sm:p-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-2xl border border-white/10 bg-[#0b1c37] text-sky-100 inline-flex items-center justify-center">
                <Shield className="h-4 w-4" />
              </div>
              <div className="text-white text-xl font-semibold tracking-tight">Rules</div>
            </div>

            <div className="mt-5 space-y-3">
              {rules.map((rule, i) => (
                <div
                  key={`${rule}-${i}`}
                  className="rounded-[24px] border border-white/12 bg-[#09182f] px-5 py-4 flex items-start gap-4"
                >
                  <div className="h-7 w-7 rounded-full border border-sky-300/20 bg-sky-400/10 text-sky-100 text-sm inline-flex items-center justify-center shrink-0">
                    {i + 1}
                  </div>
                  <div className="text-white/85 leading-relaxed">{rule}</div>
                </div>
              ))}
            </div>
          </div>
        </SectionCard>
      </div>

      <div className="xl:col-span-4 space-y-5">
        <SectionCard>
          <div className="p-5 sm:p-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-2xl border border-white/10 bg-[#0b1c37] text-sky-100 inline-flex items-center justify-center">
                <Info className="h-4 w-4" />
              </div>
              <div className="text-white text-xl font-semibold tracking-tight">
                Why About matters
              </div>
            </div>

            <div className="mt-5 text-white/75 leading-relaxed space-y-4">
              <p>This section explains what the club is, what belongs here, and what members should expect when they join.</p>
            </div>
          </div>
        </SectionCard>

        <SectionCard>
          <div className="p-5 sm:p-6">
            <div className="text-white text-xl font-semibold tracking-tight">What this adds</div>

            <div className="mt-5 text-white/75 leading-relaxed space-y-4">
              <p>A strong About section gives the club clarity and legitimacy.</p>
              <p>It helps new members immediately understand the culture, tone, and purpose of the community.</p>
            </div>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}

/* ----------------------------
   Page
----------------------------- */
/** md+: feed scroll lives inside `ClubFeedPanel`; below md, page/window scroll. */
const CLUB_UTILITY_BAR_MD_PX = 768;
/** Within this many px from the active scroll top, the utility bar stays visible. */
const CLUB_UTILITY_BAR_TOP_SHOW_PX = 56;
/**
 * Hide/show uses accumulated travel + lockout (not single-frame deltas) so trackpad
 * noise does not flip the bar every few ms.
 */
const CLUB_UTILITY_BAR_HIDE_PENDING_PX = 48;
const CLUB_UTILITY_BAR_SHOW_PENDING_PX = 40;
const CLUB_UTILITY_BAR_LOCKOUT_MS = 280;
/** Deltas smaller than this are ignored for pending travel (still advance sample Y). */
const CLUB_UTILITY_BAR_NOISE_FLOOR_PX = 2;
/**
 * Scroll-position hysteresis: after a show/hide commit, require this much extra movement
 * in scroll space before the opposite intent can arm. Stops flap when the user hovers near
 * one position and nudges up/down in place.
 */
const CLUB_UTILITY_BAR_COMMIT_HYST_PX = 56;

export default function ClubHome() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [club, setClub] = useState(null);
  const [membership, setMembership] = useState(null);

  const [loadingClub, setLoadingClub] = useState(false);

  const [err, setErr] = useState("");

  const [joining, setJoining] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [membershipActionErr, setMembershipActionErr] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState({ title: "", body: "", onConfirm: null });

  /** When true, right rail shows `ClubMembersTab`; when false, shows `ClubInfoRail`. */
  const [membersOpen, setMembersOpen] = useState(false);

  const feedPanelRef = useRef(null);
  const [feedReloadToken, setFeedReloadToken] = useState(0);
  const [createPostOpen, setCreatePostOpen] = useState(false);
  const useClubUnifiedComposer = isClubUnifiedComposerEnabled();
  const { user } = useAuth();
  const [unifiedComposerClubPickerOptions, setUnifiedComposerClubPickerOptions] = useState([]);

  useEffect(() => {
    if (!useClubUnifiedComposer || !user) {
      setUnifiedComposerClubPickerOptions([]);
      return;
    }
    let alive = true;
    fetchMyClubs()
      .then((body) => {
        if (!alive) return;
        const rows = Array.isArray(body?.clubs) ? body.clubs : [];
        setUnifiedComposerClubPickerOptions(
          rows
            .map((c) => ({
              id: Number(c.club_id ?? c.id),
              name: String(c.name || "").trim() || "Club",
              slug: c.slug,
            }))
            .filter((c) => Number.isFinite(c.id) && c.id > 0)
        );
      })
      .catch(() => {
        if (alive) setUnifiedComposerClubPickerOptions([]);
      });
    return () => {
      alive = false;
    };
  }, [useClubUnifiedComposer, user]);

  /** Club utility bar search (UI + state only; wire to API when available). */
  const [clubUtilitySearchQuery, setClubUtilitySearchQuery] = useState("");

  const clubUtilityBarRef = useRef(null);
  const [clubUtilityBarHeight, setClubUtilityBarHeight] = useState(0);
  const [utilityBarScrollCollapsed, setUtilityBarScrollCollapsed] = useState(false);
  const utilityBarScrollCollapsedRef = useRef(false);
  const utilityBarLastSampleYRef = useRef(0);
  const utilityBarPendingDownRef = useRef(0);
  const utilityBarPendingUpRef = useRef(0);
  const utilityBarLastToggleAtRef = useRef(0);
  /** Scroll Y when the bar last became visible (arms hide only below lastShow + HYST). */
  const utilityBarLastShowScrollYRef = useRef(0);
  /** Scroll Y when the bar last hid; arms show only above (lastHide - HYST) toward top. */
  const utilityBarLastHideScrollYRef = useRef(null);
  /** Bumps on window resize so feed vs window scroll listeners re-bind at the md breakpoint. */
  const [utilityBarScrollBindVersion, setUtilityBarScrollBindVersion] = useState(0);

  const draftFromNav = location.state?.clubDraft || null;
  const draftFromStorage = useMemo(() => loadClubDraft(slug), [slug]);
  const clubDraft = draftFromNav || draftFromStorage;

  const { modules, accent } = useMemo(() => {
    const serverSettings =
      club?.settings && typeof club.settings === "object" ? club.settings : {};
    const previewSettings =
      clubDraft?.settings && typeof clubDraft.settings === "object" ? clubDraft.settings : {};
    const settings =
      Object.keys(serverSettings).length > 0 ? serverSettings : previewSettings;
    const m = settings?.modules || {};
    return {
      modules: {
        artFeed: m.artFeed !== false,
        discussions: m.discussions !== false,
        channels: m.channels !== false,
        // Backward-compatible default:
        // older club settings may not include `modules.events` at all.
        // Events should still be accessible from the top action area,
        // so treat missing/undefined as enabled unless explicitly disabled.
        events: m.events !== false,
        announcements: m.announcements !== false,
        resources: m.resources !== false,
      },
      accent: settings?.accent || "emerald",
    };
  }, [club, clubDraft]);

  const allowedTabs = useMemo(() => allowedTabsForModules(modules), [modules]);
  const defaultTab = useMemo(() => defaultTabForAllowed(allowedTabs), [allowedTabs]);

  const activeTab = useMemo(() => {
    const raw = searchParams.get("tab");
    const n = normalizeTabQuery(raw);
    if (n && allowedTabs.includes(n)) return n;
    return defaultTab;
  }, [searchParams, allowedTabs, defaultTab]);

  const getWindowScrollTop = useCallback(() => {
    if (typeof window === "undefined") return 0;
    const root = document.scrollingElement ?? document.documentElement;
    return window.scrollY || root?.scrollTop || 0;
  }, []);

  /**
   * Use the feed inner scroller only when it is actually scrollable. Otherwise the
   * Clubs layout often grows with content and the *document* scrolls while inner
   * scrollTop stays 0 — the previous logic never saw movement and the bar never hid.
   */
  const getClubPageScrollTop = useCallback(() => {
    if (typeof window === "undefined") return 0;
    if (activeTab === "feed" && window.innerWidth >= CLUB_UTILITY_BAR_MD_PX) {
      const inner = feedPanelRef.current?.getFeedScrollElement?.();
      if (inner && inner.scrollHeight > inner.clientHeight + 2) {
        return inner.scrollTop;
      }
    }
    return getWindowScrollTop();
  }, [activeTab, getWindowScrollTop]);

  const setTabSearchParam = useCallback(
    (tab) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("tab", tab);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  /** Legacy share URLs: /home?tab=feed&post=<id> → full thread page */
  useEffect(() => {
    const pid = searchParams.get("post");
    if (pid == null || String(pid).trim() === "") return;
    const n = Number(pid);
    if (!Number.isFinite(n) || !slug) return;
    navigate(`/clubs/${encodeURIComponent(slug)}/posts/${n}`, { replace: true });
  }, [slug, searchParams, navigate]);

  useEffect(() => {
    let alive = true;

    setClub(null);
    setMembership(null);
    setErr("");
    setMembershipActionErr("");
    setMembersOpen(false);

    setLoadingClub(true);

    fetchClub(slug)
      .then((clubRes) => {
        if (!alive) return;
        const c = clubRes?.club ?? clubRes ?? null;
        const m = clubRes?.membership ?? null;
        setClub(c);
        setMembership(m);
      })
      .catch((e) => {
        if (!alive) return;
        setErr(e?.message || "Failed to load club.");
      })
      .finally(() => {
        if (!alive) return;
        setLoadingClub(false);
      });

    return () => {
      alive = false;
    };
  }, [slug]);

  useEffect(() => {
    setClubUtilitySearchQuery("");
  }, [slug]);

  /**
   * Keep the URL canonical: missing/invalid tab → default; wrong casing → lowercase key.
   * Runs for all viewers once the club shell is loaded (members and visitors).
   */
  useEffect(() => {
    if (loadingClub || !club) return;

    const raw = searchParams.get("tab");
    const n = normalizeTabQuery(raw);
    const urlValid = Boolean(n && allowedTabs.includes(n));
    const canonical = urlValid ? n : defaultTab;

    if (!urlValid || raw == null || String(raw).trim() === "" || String(raw) !== canonical) {
      setTabSearchParam(canonical);
    }
  }, [loadingClub, club, allowedTabs, defaultTab, searchParams, setTabSearchParam]);

  const goTab = (tab) => {
    if (!allowedTabs.includes(tab)) return;
    setTabSearchParam(tab);
  };

  const openCreatePostModal = useCallback(() => {
    if (!modules.discussions) return;
    if (String(membership?.status || "").toLowerCase() !== "active") return;
    setCreatePostOpen(true);
  }, [modules.discussions, membership]);

  const closeCreatePostModal = useCallback(() => {
    setCreatePostOpen(false);
  }, []);

  const onClubFeedPostCreated = useCallback(() => {
    setFeedReloadToken((t) => t + 1);
    if (allowedTabs.includes("feed")) {
      setTabSearchParam("feed");
    }
  }, [allowedTabs, setTabSearchParam]);

  const handleUnifiedClubComposerSuccess = useCallback(
    async (result) => {
      onClubFeedPostCreated();
      const postId = getV2CreatePostId(result);
      const post = result?.post;
      const cid = post ? Number(post.club_id ?? post.clubId) : null;
      const match =
        cid != null && Number.isFinite(cid)
          ? unifiedComposerClubPickerOptions.find((c) => Number(c.id) === cid)
          : null;
      const slugStr = String(match?.slug || club?.slug || slug || "").trim();
      if (slugStr && postId != null) {
        navigate(
          `/clubs/${encodeURIComponent(slugStr)}/posts/${encodeURIComponent(String(postId))}`
        );
      }
    },
    [club?.slug, navigate, onClubFeedPostCreated, slug, unifiedComposerClubPickerOptions]
  );

  const counts = useMemo(() => getClubCounts(club), [club]);

  const viewerUserId = useMemo(
    () =>
      pickFirstNonNull(
        user?.id,
        user?.user_id,
        user?.userId,
        user?.uid,
        user?.sub,
        user?.profile?.id,
        user?.profile?.user_id,
        user?.account?.id,
        user?.account?.user_id,
        user?.account_id,
        user?.user?.id,
        user?.user?.user_id,
        user?.data?.id
      ),
    [user]
  );

  const isActiveMember = useMemo(
    () => String(membership?.status || "").toLowerCase() === "active",
    [membership]
  );
  const isPendingMember = useMemo(
    () => String(membership?.status || "").toLowerCase() === "pending",
    [membership]
  );
  const ownerUserId = useMemo(() => getOwnerUserId(club), [club]);
  const isOwner = useMemo(
    () => isSameUserId(ownerUserId, viewerUserId),
    [ownerUserId, viewerUserId]
  );
  const isArchived = useMemo(() => getArchivedFlag(club), [club]);

  const redirectToLogin = useCallback(() => {
    navigate("/login", { state: { from: location.pathname } });
  }, [navigate, location.pathname]);

  const onJoinClub = useCallback(async () => {
    if (joining || leaving || archiving) return;
    if (!viewerUserId) {
      const ok = window.confirm("You need to log in to join this club. Go to login?");
      if (ok) redirectToLogin();
      return;
    }
    if (!slug) return;
    try {
      setJoining(true);
      setMembershipActionErr("");
      const raw = await joinClub(slug);
      setMembership(normalizeMembershipFromJoin(raw));
      setClub((prev) => bumpCountField(prev, ["member_count", "members_count", "members"], +1));
    } catch (e) {
      setMembershipActionErr(e?.message || "Failed to join.");
    } finally {
      setJoining(false);
    }
  }, [joining, leaving, archiving, slug, viewerUserId, redirectToLogin]);

  const onLeaveClub = useCallback(async () => {
    if (joining || leaving || archiving || !viewerUserId || !isActiveMember) return;
    if (isOwner) return;
    const ok = window.confirm("Leave this club?");
    if (!ok) return;
    if (!slug) return;
    try {
      setLeaving(true);
      setMembershipActionErr("");
      await leaveClub(slug);
      setMembership(null);
      setClub((prev) => bumpCountField(prev, ["member_count", "members_count", "members"], -1));
    } catch (e) {
      setMembershipActionErr(e?.message || "Failed to leave.");
    } finally {
      setLeaving(false);
    }
  }, [joining, leaving, archiving, viewerUserId, isActiveMember, isOwner, slug]);

  const onToggleArchiveClub = useCallback(async () => {
    if (!isOwner) return;
    const next = !isArchived;
    const msg = next
      ? "Delete this club? It will disappear for everyone."
      : "Restore this club so it becomes visible again?";
    setConfirmConfig({
      title: next ? "Delete club?" : "Restore club?",
      body: msg,
      onConfirm: async () => {
        setConfirmOpen(false);
        try {
          setArchiving(true);
          setMembershipActionErr("");
          setClub((prev) => (prev ? { ...prev, is_deleted: next } : prev));
          await archiveClub(slug, next);
          if (next) {
            navigate("/clubs/discover");
            return;
          }
          const fresh = await fetchClub(slug);
          setClub(fresh?.club ?? fresh ?? null);
          setMembership(fresh?.membership ?? null);
        } catch (e) {
          setClub((prev) => (prev ? { ...prev, is_deleted: isArchived } : prev));
          setMembershipActionErr(e?.message || "Failed to update club.");
        } finally {
          setArchiving(false);
        }
      },
    });
    setConfirmOpen(true);
  }, [isOwner, isArchived, slug, navigate]);

  const clubName = club?.name ?? "Club";

  const clubTagline =
    club?.status ?? club?.tagline ?? club?.subtitle ?? club?.headline ?? "";

  const avatar = resolveImageUrl(
    clubDraft?.avatar_image_url ||
      club?.avatar_image_url ||
      club?.avatarImageUrl ||
      club?.icon_url ||
      null
  );

  /** Top utility/search bar once club is loaded (in-flow sticky inside AppShell main column). */
  const reserveClubTopBarSpace = Boolean(club && !loadingClub);

  /**
   * Right column on xl: same grid slot as pre–ClubInfoRail builds (fixed two-column shell
   * whenever the club workspace is mounted). `membersOpen` only swaps inner content
   * between `ClubInfoRail` and `ClubMembersTab`; it no longer removes the column.
   * Hidden on Channels (that surface owns members UI).
   */
  const showHomeMembersRail = activeTab !== "channels" && reserveClubTopBarSpace;

  const clubUtilityBarVisible = reserveClubTopBarSpace && !createPostOpen;

  useLayoutEffect(() => {
    if (!clubUtilityBarVisible) {
      setClubUtilityBarHeight(0);
      return undefined;
    }
    const el = clubUtilityBarRef.current;
    if (!el) {
      setClubUtilityBarHeight(0);
      return undefined;
    }
    const measure = () => {
      setClubUtilityBarHeight(Math.round(el.getBoundingClientRect().height));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [clubUtilityBarVisible]);

  const utilityBarEffectiveTop = useMemo(() => {
    if (!clubUtilityBarVisible) return 0;
    if (utilityBarScrollCollapsed) return 0;
    return clubUtilityBarHeight;
  }, [clubUtilityBarVisible, utilityBarScrollCollapsed, clubUtilityBarHeight]);

  useEffect(() => {
    utilityBarScrollCollapsedRef.current = utilityBarScrollCollapsed;
  }, [utilityBarScrollCollapsed]);

  useEffect(() => {
    if (createPostOpen) {
      utilityBarScrollCollapsedRef.current = false;
      setUtilityBarScrollCollapsed(false);
    }
  }, [createPostOpen]);

  useEffect(() => {
    const onResize = () => setUtilityBarScrollBindVersion((v) => v + 1);
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!clubUtilityBarVisible) {
      utilityBarScrollCollapsedRef.current = false;
      setUtilityBarScrollCollapsed(false);
      return undefined;
    }

    const y0 = getClubPageScrollTop();
    utilityBarLastSampleYRef.current = y0;
    utilityBarPendingDownRef.current = 0;
    utilityBarPendingUpRef.current = 0;
    utilityBarLastShowScrollYRef.current = y0;
    utilityBarLastHideScrollYRef.current = null;

    let rafId = 0;
    const onScroll = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        const y = getClubPageScrollTop();
        const rawDy = y - utilityBarLastSampleYRef.current;
        utilityBarLastSampleYRef.current = y;
        const dy =
          Math.abs(rawDy) < CLUB_UTILITY_BAR_NOISE_FLOOR_PX ? 0 : rawDy;

        if (y <= CLUB_UTILITY_BAR_TOP_SHOW_PX) {
          utilityBarPendingDownRef.current = 0;
          utilityBarPendingUpRef.current = 0;
          utilityBarLastShowScrollYRef.current = y;
          utilityBarLastHideScrollYRef.current = null;
          if (utilityBarScrollCollapsedRef.current) {
            utilityBarScrollCollapsedRef.current = false;
            setUtilityBarScrollCollapsed(false);
          }
          return;
        }

        const now = Date.now();
        const sinceToggle = now - utilityBarLastToggleAtRef.current;
        const collapsed = utilityBarScrollCollapsedRef.current;
        const hyst = CLUB_UTILITY_BAR_COMMIT_HYST_PX;

        if (!collapsed) {
          utilityBarPendingUpRef.current = 0;
          const showY = utilityBarLastShowScrollYRef.current;
          if (y < showY + hyst) {
            utilityBarPendingDownRef.current = 0;
          } else {
            if (dy > 0) utilityBarPendingDownRef.current += dy;
            else
              utilityBarPendingDownRef.current = Math.max(
                0,
                utilityBarPendingDownRef.current + dy
              );
            if (
              utilityBarPendingDownRef.current >= CLUB_UTILITY_BAR_HIDE_PENDING_PX &&
              sinceToggle >= CLUB_UTILITY_BAR_LOCKOUT_MS
            ) {
              utilityBarPendingDownRef.current = 0;
              utilityBarLastToggleAtRef.current = now;
              utilityBarLastHideScrollYRef.current = y;
              utilityBarScrollCollapsedRef.current = true;
              setUtilityBarScrollCollapsed(true);
            }
          }
        } else {
          utilityBarPendingDownRef.current = 0;
          const hideY = utilityBarLastHideScrollYRef.current;
          if (hideY == null) {
            utilityBarPendingUpRef.current = 0;
          } else if (y > hideY - hyst) {
            utilityBarPendingUpRef.current = 0;
          } else {
            if (dy < 0) utilityBarPendingUpRef.current += -dy;
            else
              utilityBarPendingUpRef.current = Math.max(
                0,
                utilityBarPendingUpRef.current - dy
              );
            if (
              utilityBarPendingUpRef.current >= CLUB_UTILITY_BAR_SHOW_PENDING_PX &&
              sinceToggle >= CLUB_UTILITY_BAR_LOCKOUT_MS
            ) {
              utilityBarPendingUpRef.current = 0;
              utilityBarLastToggleAtRef.current = now;
              utilityBarLastShowScrollYRef.current = y;
              utilityBarLastHideScrollYRef.current = null;
              utilityBarScrollCollapsedRef.current = false;
              setUtilityBarScrollCollapsed(false);
            }
          }
        }
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    const feedEl = feedPanelRef.current?.getFeedScrollElement?.();
    let detachFeed = () => {};
    let detachFeedRo = () => {};
    if (
      activeTab === "feed" &&
      feedEl &&
      typeof window !== "undefined" &&
      window.innerWidth >= CLUB_UTILITY_BAR_MD_PX
    ) {
      feedEl.addEventListener("scroll", onScroll, { passive: true });
      detachFeed = () => feedEl.removeEventListener("scroll", onScroll);
      const ro = new ResizeObserver(() => {
        const yn = getClubPageScrollTop();
        utilityBarLastSampleYRef.current = yn;
        utilityBarPendingDownRef.current = 0;
        utilityBarPendingUpRef.current = 0;
        utilityBarLastShowScrollYRef.current = yn;
        utilityBarLastHideScrollYRef.current = null;
      });
      ro.observe(feedEl);
      detachFeedRo = () => ro.disconnect();
    }

    return () => {
      window.removeEventListener("scroll", onScroll);
      detachFeed();
      detachFeedRo();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [
    clubUtilityBarVisible,
    activeTab,
    getClubPageScrollTop,
    club,
    utilityBarScrollBindVersion,
  ]);

  const renderTabContent = () => {
    if (loadingClub) {
      return (
        <div className="p-4 sm:p-5 xl:p-6">
          <SectionCard>
            <div className="p-6 text-white/60">Loading club…</div>
          </SectionCard>
        </div>
      );
    }

    if (!club) {
      return (
        <div className="p-4 sm:p-5 xl:p-6">
          <EmptyPanel
            title="Club not found"
            body="We couldn’t find this club or it may no longer be available."
          />
        </div>
      );
    }

    if (activeTab === "feed") {
      if (!modules.discussions) {
        return (
          <div className="p-4 sm:p-5 xl:p-6">
            <EmptyPanel
              title="Feed disabled"
              body="This club has turned off the discussion feed."
            />
          </div>
        );
      }
      return (
        <div className="flex flex-col flex-1 min-h-0 p-4 sm:p-5 xl:p-6">
          <ClubFeedPanel
            ref={feedPanelRef}
            club={club}
            clubSlug={slug}
            membership={membership}
            accent={accent}
            feedReloadToken={feedReloadToken}
            className="md:flex-1 md:min-h-0"
          />
        </div>
      );
    }

    if (activeTab === "gallery") {
      if (!modules.artFeed) {
        return (
          <div className="p-4 sm:p-5 xl:p-6">
            <EmptyPanel title="Gallery disabled" body="This club has disabled the art gallery tab." />
          </div>
        );
      }
      return <ClubGalleryTab slug={slug} />;
    }

    if (activeTab === "channels") {
      if (!modules.channels) {
        return (
          <div className="p-4 sm:p-5 xl:p-6">
            <EmptyPanel title="Channels disabled" body="This club has disabled channels." />
          </div>
        );
      }
      return (
        <div className="min-h-[520px] p-4 sm:p-5 xl:p-6">
          <ClubChannelsEnvironment
            slug={slug}
            club={club}
            membership={membership}
            accent={accent}
            membersOpen={membersOpen}
            onMembersOpenChange={setMembersOpen}
          />
        </div>
      );
    }

    if (activeTab === "events") {
      return <ClubEventsTab slug={slug} club={club} counts={counts} membership={membership} />;
    }

    return <ClubAboutTab club={club} counts={counts} draft={clubDraft} />;
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-[#020817] text-white">
      {!reserveClubTopBarSpace ? (
        <div className="border-b border-white/10 bg-[#020817]">
          <div className="mx-auto max-w-[1600px] px-4 py-4 sm:px-6 sm:py-5 xl:px-8">
            <div className="text-sm text-white/55">{loadingClub ? "Loading club…" : null}</div>
            {err ? (
              <div className="mt-4 rounded-2xl border border-red-400/20 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                {err}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {reserveClubTopBarSpace && !createPostOpen ? (
        <div
          ref={clubUtilityBarRef}
          className={cn(
            "sticky top-0 z-[9996] pt-[env(safe-area-inset-top,0px)]",
            "bg-[#020817]/[0.96] backdrop-blur-xl",
            "transition-[transform,opacity,margin-bottom] duration-300 ease-out",
            utilityBarScrollCollapsed &&
              "-translate-y-full opacity-0 pointer-events-none"
          )}
          style={{
            marginBottom:
              utilityBarScrollCollapsed && clubUtilityBarHeight > 0
                ? -clubUtilityBarHeight
                : 0,
          }}
          role="region"
          aria-label="Club controls"
          aria-hidden={utilityBarScrollCollapsed}
        >
          <div className="mx-auto max-w-[1600px] px-3 sm:px-5 xl:px-8 py-2.5 sm:py-3">
            <div
              className={cn(
                "flex w-full min-w-0 items-center gap-3 sm:gap-4 md:gap-5 lg:gap-6",
                "rounded-xl border border-white/12 bg-[#0a1629]/90 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]",
                "px-3 py-2 sm:px-5 sm:py-2.5"
              )}
            >
              <IconButton
                title="Back"
                aria-label="Go back"
                onClick={() => navigate(-1)}
                className="h-8 w-8 shrink-0 rounded-lg border-white/[0.08] bg-white/[0.04] hover:border-white/15 hover:bg-white/[0.08] sm:h-9 sm:w-9"
              >
                <ArrowLeft className="h-4 w-4" />
              </IconButton>

              <form
                className="min-w-[min(100%,10rem)] flex-1 basis-0 sm:min-w-[14rem] md:min-w-[20rem] lg:min-w-[22rem]"
                role="search"
                onSubmit={(e) => {
                  e.preventDefault();
                }}
              >
                <label htmlFor="club-utility-search" className="sr-only">
                  Search this club
                </label>
                <div className="flex h-8 min-w-0 w-full items-center gap-2.5 rounded-full bg-white/[0.06] px-3.5 transition-colors sm:h-9 sm:gap-3 sm:px-4 focus-within:bg-white/[0.1]">
                  <Search className="h-3.5 w-3.5 shrink-0 text-white/35 sm:h-4 sm:w-4" aria-hidden />
                  <input
                    id="club-utility-search"
                    type="search"
                    name="clubUtilitySearch"
                    autoComplete="off"
                    value={clubUtilitySearchQuery}
                    onChange={(e) => setClubUtilitySearchQuery(e.target.value)}
                    placeholder="Search posts, channels, members…"
                    className={cn(
                      "min-w-0 flex-1 appearance-none rounded-none border-0 bg-transparent p-0",
                      "text-[13px] text-white/95 shadow-none ring-0 ring-offset-0",
                      "outline-none placeholder:text-white/30",
                      "focus:border-0 focus:outline-none focus:ring-0 focus:ring-offset-0 focus:shadow-none",
                      "sm:text-sm",
                      "[&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none",
                      "[&::-webkit-search-results-button]:appearance-none [&::-webkit-search-results-decoration]:appearance-none"
                    )}
                  />
                </div>
              </form>

              <div className="flex min-w-0 max-w-[min(100%,17rem)] shrink-0 flex-nowrap items-center justify-end gap-1.5 overflow-x-auto overflow-y-visible scrollbar-hide sm:max-w-none sm:gap-2 md:gap-2.5 md:pl-1">
                {modules.discussions && isActiveMember ? (
                  <IconButton
                    title="New club feed post"
                    aria-label="New club feed post"
                    onClick={openCreatePostModal}
                    className="h-8 w-8 shrink-0 rounded-lg border-white/[0.08] bg-white/[0.04] hover:border-white/15 hover:bg-white/[0.08] sm:h-9 sm:w-9"
                  >
                    <Plus className="h-4 w-4" />
                  </IconButton>
                ) : null}

                {modules.discussions ? (
                  <TopActionButton
                    active={activeTab === "feed"}
                    title="Club feed"
                    onClick={() => goTab("feed")}
                    className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg px-2 text-[11px] sm:h-9 sm:px-2.5 sm:text-xs"
                  >
                    <MessageSquareText className="h-3.5 w-3.5 shrink-0" />
                    <span className="hidden sm:inline">Feed</span>
                  </TopActionButton>
                ) : null}

                {modules.channels ? (
                  <TopActionButton
                    active={activeTab === "channels"}
                    onClick={() => goTab("channels")}
                    className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg px-2 text-[11px] sm:h-9 sm:px-2.5 sm:text-xs"
                  >
                    <Hash className="h-3.5 w-3.5 shrink-0" />
                    <span className="hidden sm:inline">Channels</span>
                  </TopActionButton>
                ) : null}

                {modules.events ? (
                  <TopActionButton
                    active={activeTab === "events"}
                    onClick={() => goTab("events")}
                    className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg px-2 text-[11px] sm:h-9 sm:px-2.5 sm:text-xs"
                  >
                    <CalendarDays className="h-3.5 w-3.5 shrink-0" />
                    <span className="hidden sm:inline">Events</span>
                  </TopActionButton>
                ) : null}

                <TopActionButton
                  active={membersOpen}
                  title="Members"
                  onClick={() => setMembersOpen((v) => !v)}
                  className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg px-2 text-[11px] sm:h-9 sm:px-2.5 sm:text-xs"
                >
                  <Users className="h-3.5 w-3.5 shrink-0" />
                  <span className="hidden sm:inline">Members</span>
                </TopActionButton>

                <IconButton
                  title="About this club"
                  aria-label="About this club"
                  onClick={() => goTab("about")}
                  className={cn(
                    "h-8 w-8 shrink-0 rounded-lg sm:h-9 sm:w-9",
                    activeTab === "about"
                      ? "border-sky-300/25 bg-sky-400/10 text-sky-100 hover:border-sky-300/35 hover:bg-sky-400/15"
                      : "border-white/[0.08] bg-white/[0.04] hover:border-white/15 hover:bg-white/[0.08]"
                  )}
                >
                  <Info className="h-4 w-4" />
                </IconButton>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="mx-auto flex w-full max-w-[1600px] flex-1 min-h-0 flex-col px-3 sm:px-5 xl:px-8">
        <div
          className={cn(
            "grid min-h-[calc(100vh-190px)] flex-1 min-w-0",
            showHomeMembersRail
              ? "xl:grid-cols-[minmax(0,1fr)_340px]"
              : "grid-cols-1"
          )}
        >
          <main className="min-w-0 min-h-0 flex flex-col">{renderTabContent()}</main>

          {showHomeMembersRail ? (
            <aside
              className="hidden min-w-0 self-start overflow-y-auto xl:sticky xl:block py-4 sm:py-5 xl:py-6 pl-4 sm:pl-5 xl:pl-6 pr-3 sm:pr-5 xl:pr-5 transition-[top,max-height] duration-300 ease-out"
              style={{
                top: utilityBarEffectiveTop,
                maxHeight: `calc(100dvh - ${utilityBarEffectiveTop}px)`,
              }}
            >
              <ClubInfoRail
                club={club}
                slug={String(slug || "").trim()}
                accent={accent}
                avatarUrl={avatar}
                clubName={clubName}
                tagline={clubTagline}
                counts={counts}
                isOwner={isOwner}
                isArchived={isArchived}
                isActiveMember={isActiveMember}
                isPendingMember={isPendingMember}
                joining={joining}
                leaving={leaving}
                archiving={archiving}
                membershipActionErr={membershipActionErr}
                onJoin={onJoinClub}
                onLeave={onLeaveClub}
                onToggleArchive={onToggleArchiveClub}
                onOpenAbout={() => goTab("about")}
                railExtras={{}}
              />
              {membersOpen ? (
                <div className="mt-5 border-t border-white/10 pt-5">
                  <ClubMembersTab club={club} />
                </div>
              ) : null}
            </aside>
          ) : null}
        </div>
      </div>

      {showHomeMembersRail && membersOpen ? (
        <div
          className="xl:hidden fixed inset-x-0 bottom-0 z-[9998] transition-[top] duration-300 ease-out"
          style={{
            top: utilityBarEffectiveTop,
          }}
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setMembersOpen(false)}
            aria-label="Close club sidebar"
          />

          <div className="absolute right-0 top-0 h-full w-[92vw] max-w-[400px] overflow-y-auto bg-[#030b18] p-3">
            <ClubInfoRail
              club={club}
              slug={String(slug || "").trim()}
              accent={accent}
              avatarUrl={avatar}
              clubName={clubName}
              tagline={clubTagline}
              counts={counts}
              isOwner={isOwner}
              isArchived={isArchived}
              isActiveMember={isActiveMember}
              isPendingMember={isPendingMember}
              joining={joining}
              leaving={leaving}
              archiving={archiving}
              membershipActionErr={membershipActionErr}
              onJoin={onJoinClub}
              onLeave={onLeaveClub}
              onToggleArchive={onToggleArchiveClub}
              onOpenAbout={() => goTab("about")}
              railExtras={{}}
            />
            <div className="mt-4 border-t border-white/10 pt-4">
              <ClubMembersTab club={club} />
            </div>
          </div>
        </div>
      ) : null}

      {createPostOpen && club ? (
        useClubUnifiedComposer ? (
          <UnifiedPostComposer
            isOpen={createPostOpen}
            onClose={closeCreatePostModal}
            surface="club"
            club={{
              id: n(club.club_id ?? club.id),
              club_id: n(club.club_id ?? club.id),
              name: club.name,
              slug: club.slug,
            }}
            membership={membership}
            clubPickerOptions={unifiedComposerClubPickerOptions}
            defaultTargetClubId={n(club.club_id ?? club.id)}
            headlineTitle="Create post"
            onSuccess={handleUnifiedClubComposerSuccess}
          />
        ) : (
          <ClubFeedCreatePostModal
            club={club}
            membership={membership}
            onClose={closeCreatePostModal}
            onPosted={onClubFeedPostCreated}
          />
        )
      ) : null}

      {confirmOpen ? (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setConfirmOpen(false)}
            aria-label="Close"
          />
          <div className="relative z-[10000] w-full max-w-md rounded-2xl border border-white/12 bg-black/85 backdrop-blur-xl p-5">
            <div className="text-white font-semibold text-lg">{confirmConfig.title}</div>
            <div className="mt-2 text-sm text-white/70">{confirmConfig.body}</div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                className="h-11 px-4 rounded-xl border border-white/14 bg-white/[0.03] text-white/80"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => confirmConfig.onConfirm?.()}
                className="h-11 px-5 rounded-xl border border-red-400/30 bg-red-500/15 text-red-100"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}